import { from, Observable, switchMap } from 'rxjs'
import { map } from 'rxjs/operators'

import { getBlobBase64 } from '@procom-labs/common'

import { environment } from '@resume-portal/environment'
import { axios } from '@resume-portal/lib'
import {
  IAddTemplate,
  IRemainingFreeTemplates,
  ITemplate,
  TemplateStatuses,
} from '@resume-portal/models'
import { resumeBuilderStore } from '@resume-portal/stores'

interface IThumbnailsStorage {
  organizationId: string
  templates: IThumbnailCached[]
}

interface IThumbnailCached {
  fileGuid: string | null
  url: string | null
}

const storageKey = 'templateThumbnails'

class ResumeTemplateService {
  private readonly baseUrl = `${environment.GORILLA_RESUME_API_URL}/ResumeTemplate`

  getTemplates(orgId: string): Observable<ITemplate[]> {
    return axios.get<ITemplate[]>(`${this.baseUrl}/organization/${orgId}`).pipe(
      map(({ data }) => {
        resumeBuilderStore.dispatch({ templates: data })
        return data
      })
    )
  }

  editTemplateInfo(
    id: string,
    organizationId: string,
    name: string,
    teamIds: string[]
  ): Observable<ITemplate> {
    return axios
      .put<ITemplate>(`${this.baseUrl}/${id}/info`, {
        name,
        organizationId,
        teamIds,
      })
      .pipe(map(({ data }) => data))
  }

  createOrEditTemplate(
    organizationId: string,
    payload: IAddTemplate,
    status: TemplateStatuses,
    id?: string
  ): Observable<ITemplate> {
    const formData = new FormData()
    if (payload.file) {
      formData.append('File', payload.file)
    }
    formData.append('OrganizationId', organizationId)
    formData.append('Name', payload.name)
    formData.append('Instructions', payload.instructions)
    formData.append('Status', status)
    payload.teamIds.forEach((item) => formData.append('teamIds[]', item))

    const request =
      status === TemplateStatuses.EditRequest && id
        ? axios.put<ITemplate>(`${this.baseUrl}/${id}`, formData)
        : axios.post<ITemplate>(this.baseUrl, formData)

    return request.pipe(
      map(({ data }) => {
        let currTemplates = resumeBuilderStore.getStateValue().templates
        if (status === TemplateStatuses.EditRequest && id) {
          currTemplates = currTemplates.map((temp) =>
            temp.id === id ? data : temp
          )
        } else {
          currTemplates = [...currTemplates, data]
        }
        resumeBuilderStore.dispatch({ templates: currTemplates })
        return data
      })
    )
  }

  deleteTemplate(id: string, organizationId: string): Observable<void> {
    return axios
      .delete<void>(`${this.baseUrl}/${id}?organizationId=${organizationId}`)
      .pipe(map(() => {}))
  }

  getThumbnailsCached(organizationId: string): IThumbnailsStorage | null {
    const thumbnailsCached = sessionStorage.getItem(storageKey)
    if (thumbnailsCached) {
      const parsedThumbnails = JSON.parse(thumbnailsCached)
      if (parsedThumbnails.organizationId === organizationId) {
        return parsedThumbnails
      }
    }
    sessionStorage.setItem(
      storageKey,
      JSON.stringify({ organizationId, templates: [] })
    )
    const newThumbnailsCached = sessionStorage.getItem(storageKey)
    return newThumbnailsCached ? JSON.parse(newThumbnailsCached) : null
  }

  getThumbnailImage(
    id: string,
    templateOrgId: string,
    fileGuid: string,
    userOrgId: string
  ): Observable<string> {
    // Get all thumbnails from session storage
    const thumbnailsCached = this.getThumbnailsCached(userOrgId)
    const thumbnailImage = thumbnailsCached?.templates?.find(
      (thumb) => thumb.fileGuid === fileGuid
    )
    if (thumbnailImage?.url) {
      return from([thumbnailImage.url])
    }
    return axios
      .get<ArrayBuffer>(
        `${this.baseUrl}/${id}/thumbnail?organizationId=${templateOrgId}`,
        {
          responseType: 'arraybuffer',
        }
      )
      .pipe(
        switchMap(async ({ data }) => {
          const url = await getBlobBase64(data)
          if (typeof url !== 'string') throw new Error('Thumbnail not valid')
          const thumbCached = this.getThumbnailsCached(userOrgId)
          thumbCached?.templates.push({ fileGuid, url })
          try {
            sessionStorage.setItem(storageKey, JSON.stringify(thumbCached))
          } catch (error) {
            return url
          }
          return url
        })
      )
  }

  getRemainingFreeTemplates(
    organizationId: string
  ): Observable<IRemainingFreeTemplates> {
    return axios
      .get<IRemainingFreeTemplates>(
        `${this.baseUrl}/organization/${organizationId}/remaining-free-templates`
      )
      .pipe(
        map(({ data }) => {
          return data
        })
      )
  }
}

export const resumeTemplateService = new ResumeTemplateService()
